import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Plotly from 'plotly.js-dist';
import Papa from 'papaparse';
import { scaleLinear } from 'd3-scale';
import HVHSViz from './hvhsvisualizer';
import CountryTypeahead from './CountryTypeahead';

// CR feedback (Sep 2026): the Indicator selector is organised into three
// subcategories drawn from the OECD_Dataset taxonomy
// (System Components > Sub-Components). Each subcategory fixes which axis the
// HVHS score sits on:
//   'y' → HVHS is the dependent variable (indicator on X)
//   'x' → HVHS is the independent variable (indicator on Y)
const METRIC_GROUPS = [
  {
    key: 'context',
    label: 'Health System Context',
    hvhsAxis: 'y',
    taxonomy: [
      ['Health System Context', 'Economic'],
      ['Health System Functions', 'Financing']
    ]
  },
  {
    key: 'outcomes',
    label: 'Health System Outcomes',
    hvhsAxis: 'x',
    taxonomy: [
      ['Health System Outcomes', 'Health'],
      // CR wrote "health system functions > financial risk protection"; in the
      // dataset taxonomy Financial Risk Protection sits under Health System
      // Outcomes. Taken from the data.
      ['Health System Outcomes', 'Financial Risk Protection']
    ]
  },
  {
    key: 'objectives',
    label: 'Health System Output Objectives',
    hvhsAxis: 'x',
    taxonomy: [
      ['Health System Objectives', 'Effectiveness'],
      ['Health System Objectives', 'Efficiency'],
      ['Health System Objectives', 'Equity'],
      ['Health System Objectives', 'Responsiveness']
    ]
  }
];

const GROUP_LABELS = METRIC_GROUPS.map(g => g.label);

// taxonomy pair → group key
const TAXONOMY_TO_GROUP = {};
METRIC_GROUPS.forEach(g => {
  g.taxonomy.forEach(([comp, sub]) => { TAXONOMY_TO_GROUP[`${comp}||${sub}`] = g.key; });
});

// Derived indicator kept from the previous build: GDP per capita is computed
// from FiscalSpace.csv (GDP / population) and is the default X indicator.
const DERIVED_METRICS = [
  { key: 'gdpPerCapita', label: 'Gross domestic product (GDP) per capita, US dollars', groupKey: 'context' }
];

// OECD_Dataset.csv country names that country_iso_codes.csv does not resolve.
const OECD_COUNTRY_ISO_ALIASES = {
  'Korea': 'KOR',
  'South Korea': 'KOR',
  'North Macedonia': 'MKD',
  'Russia': 'RUS',
  'Slovak Republic': 'SVK',
  'St. Kitts and Nevis': 'KNA',
  'St. Lucia': 'LCA',
  'St. Vincent and The Grenadines': 'VCT',
  'Türkiye': 'TUR',
  'United Kingdom': 'GBR',
  'United Republic of Tanzania': 'TZA',
  'United States': 'USA',
  'Occupied Palestinian Territory, including East Jerusalem': 'PSE'
};

const parseNumeric = (raw) => {
  if (raw == null) return null;
  const cleaned = String(raw).replace(/[$,\s%]/g, '');
  if (!cleaned) return null;
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
};

const metricKeyFor = (groupKey, variable) => `oecd::${groupKey}::${variable}`;

const normaliseCountryName = (name) =>
  String(name || '').trim().replace(/\s*\(the\)$/i, '').toLowerCase();

// Parse OECD_Dataset.csv into (a) an indicator catalogue derived from the
// taxonomy and (b) latest-year values keyed by ISO3 code.
const buildOecdMetrics = (csvText, isoRows) => {
  if (!csvText) return { metrics: [], values: {} };

  const nameToIso = {};
  isoRows.forEach(row => {
    if (row.Country && row.ISO3) nameToIso[normaliseCountryName(row.Country)] = row.ISO3.trim();
  });
  Object.entries(OECD_COUNTRY_ISO_ALIASES).forEach(([name, iso]) => {
    nameToIso[normaliseCountryName(name)] = iso;
  });

  const parsed = Papa.parse(csvText, { header: true, skipEmptyLines: true });
  const metricMeta = new Map();   // metricKey → { key, label, group, groupKey, unit }
  const values = {};              // iso → { metricKey: { value, year } }

  (parsed.data || []).forEach(row => {
    const comp = (row['System Components'] || '').trim();
    const sub = (row['Sub-Components'] || '').trim();
    const groupKey = TAXONOMY_TO_GROUP[`${comp}||${sub}`];
    if (!groupKey) return;

    const variable = (row.Variable || '').trim();
    if (!variable) return;
    const iso = nameToIso[normaliseCountryName(row.Country)];
    if (!iso) return;
    const value = parseNumeric(row.Value);
    if (value == null) return;
    const year = parseInt(row.Year, 10);

    const key = metricKeyFor(groupKey, variable);
    if (!metricMeta.has(key)) {
      const group = METRIC_GROUPS.find(g => g.key === groupKey);
      metricMeta.set(key, {
        key,
        label: variable,
        group: group.label,
        groupKey,
        unit: (row['Unit of Measure'] || '').trim()
      });
    }

    if (!values[iso]) values[iso] = {};
    const existing = values[iso][key];
    if (!existing || !(existing.year > (isNaN(year) ? -Infinity : year))) {
      values[iso][key] = { value, year: isNaN(year) ? -Infinity : year };
    }
  });

  const metrics = Array.from(metricMeta.values())
    .sort((a, b) => a.label.localeCompare(b.label));
  return { metrics, values };
};

const HVHS_COMPONENTS = [
  { key: 'overall', label: 'Overall high value health system score', variable: 'Overall' },
  ...HVHSViz.section_names.map((name, i) => ({
    key: `c${i + 1}`,
    label: `C${i + 1}: ${name}`,
    variable: name
  }))
];

// Colors for components (color per component)
const COMPONENT_COLORS = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
  '#EC4899', '#06B6D4', '#F97316', '#14B8A6', '#6366F1'
];

// Plotly marker symbols for countries (shape per country)
const COUNTRY_SYMBOLS = [
  'circle', 'square', 'diamond', 'triangle-up', 'triangle-down',
  'star', 'hexagon', 'cross', 'x', 'pentagon', 'hexagram'
];

// B7: Categorical palette distinct from red-yellow-green performance gradient
const INCOME_COLORS = {
  'High income': '#6366F1',
  'Upper middle income': '#0EA5E9',
  'Lower middle income': '#F97316',
  'Low income': '#A855F7'
};

const INCOME_SYMBOLS = {
  'High income': 'circle',
  'Upper middle income': 'square',
  'Lower middle income': 'diamond',
  'Low income': 'triangle-up'
};

const REGION_COLORS = {
  'East Asia & Pacific': '#6366F1',
  'Europe & Central Asia': '#0EA5E9',
  'Latin America & Caribbean': '#8B5CF6',
  'Middle East & North Africa': '#F97316',
  'North America': '#EC4899',
  'South Asia': '#A855F7',
  'Sub-Saharan Africa': '#0891B2'
};

const REGION_SYMBOLS = {
  'East Asia & Pacific': 'circle',
  'Europe & Central Asia': 'square',
  'Latin America & Caribbean': 'diamond',
  'Middle East & North Africa': 'triangle-up',
  'North America': 'star',
  'South Asia': 'triangle-down',
  'Sub-Saharan Africa': 'hexagon'
};

const ACCENT = '#5ec4d4';
const LABEL_ALL_THRESHOLD = 12;

// Number formatting for indicator axes: thousands separators, no stray decimals.
const fmtFull = (v, isCurrency) => {
  if (v == null || isNaN(v)) return 'N/A';
  const abs = Math.abs(v);
  const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  const s = v.toLocaleString('en-US', { maximumFractionDigits: digits });
  return isCurrency ? (v < 0 ? '-$' + s.slice(1) : '$' + s) : s;
};

const fmtCompact = (v, isCurrency) => {
  if (v == null || isNaN(v)) return '';
  const abs = Math.abs(v);
  const trim = (n) => n.toLocaleString('en-US', { maximumFractionDigits: n >= 100 ? 0 : n >= 10 ? 1 : 2 });
  let s;
  if (abs >= 1e9) s = trim(abs / 1e9) + 'B';
  else if (abs >= 1e6) s = trim(abs / 1e6) + 'M';
  else if (abs >= 1e4) s = trim(abs / 1e3) + 'k';
  else s = trim(abs);
  return (v < 0 ? '-' : '') + (isCurrency ? '$' : '') + s;
};

const linearTicks = (lo, hi) => {
  if (!(hi > lo)) return [lo];
  return scaleLinear().domain([lo, hi]).ticks(6);
};

const logTicks = (lo, hi) => {
  const minExp = Math.floor(Math.log10(lo));
  const maxExp = Math.ceil(Math.log10(hi));
  const decades = maxExp - minExp;
  const mults = decades <= 1 ? [1, 2, 3, 5, 7] : decades <= 3 ? [1, 2, 5] : [1];
  const ticks = [];
  for (let e = minExp; e <= maxExp; e++) {
    mults.forEach(m => {
      const t = m * Math.pow(10, e);
      if (t >= lo / 1.0001 && t <= hi * 1.0001) ticks.push(t);
    });
  }
  return ticks.length > 0 ? ticks : [lo, hi];
};

const sidebarHeading = {
  fontSize: '0.75rem', fontWeight: '700', color: '#E2E8F0', margin: '0 0 0.5rem 0',
  textTransform: 'uppercase', letterSpacing: '0.05em'
};

const pillButton = (active, disabled) => ({
  padding: '0.375rem 0.75rem', fontSize: '0.8125rem', fontWeight: active ? '600' : '500',
  border: '1px solid', borderColor: active ? ACCENT : 'rgba(255,255,255,0.25)',
  backgroundColor: active ? ACCENT : 'rgba(255,255,255,0.08)',
  color: active ? '#0a2029' : disabled ? 'rgba(255,255,255,0.35)' : '#E2E8F0',
  borderRadius: '999px', cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1, minHeight: '32px'
});

const smallAction = (color) => ({
  fontSize: '0.75rem', fontWeight: '600', color, background: 'white',
  border: `1px solid ${color}`, borderRadius: '999px', padding: '0.125rem 0.625rem',
  cursor: 'pointer', minHeight: '26px'
});

// Reusable dropdown with multi-select checkboxes
const MultiSelectDropdown = ({ label, options, selected, onChange, color }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectedLabels = options.filter(o => selected.has(o.key)).map(o => o.label);
  const summary = selectedLabels.length === 0 ? 'None selected'
    : selectedLabels.length === 1 ? selectedLabels[0]
    : `${selectedLabels.length} selected`;

  return (
    <div ref={ref} style={{ position: 'relative' }} onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', padding: '0.625rem 0.875rem', fontSize: '0.9375rem',
          border: `1px solid ${open ? color : '#D1D5DB'}`, borderRadius: '6px',
          backgroundColor: 'white', cursor: 'pointer', textAlign: 'left',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          color: '#374151'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
        <span aria-hidden="true" style={{ fontSize: '0.625rem', color: '#9CA3AF', marginLeft: '0.5rem' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          backgroundColor: 'white', border: '1px solid #D1D5DB', borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '280px', overflowY: 'auto',
          marginTop: '2px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '0.5rem 0.625rem', borderBottom: '1px solid #F3F4F6', position: 'sticky', top: 0, backgroundColor: 'white' }}>
            <button type="button" onClick={() => onChange(new Set(options.map(o => o.key)))} style={smallAction('#113743')}>All</button>
            <button type="button" onClick={() => onChange(new Set())} style={smallAction('#B91C1C')}>None</button>
          </div>
          {options.map(opt => (
            <label key={opt.key} style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.625rem',
              cursor: 'pointer', fontSize: '0.9375rem', color: '#374151',
              backgroundColor: selected.has(opt.key) ? `${color}15` : 'transparent'
            }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${color}10`}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selected.has(opt.key) ? `${color}15` : 'transparent'}
            >
              <input type="checkbox" checked={selected.has(opt.key)} onChange={() => {
                const next = new Set(selected);
                if (next.has(opt.key)) next.delete(opt.key); else next.add(opt.key);
                onChange(next);
              }} style={{ accentColor: color }} spellCheck="false" />
              {opt.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// Single-select dropdown
const SingleSelectDropdown = ({ label, options, value, onChange, color, groups }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const currentLabel = options.find(o => o.key === value)?.label || 'Select...';

  // Group options if groups provided
  const groupedOptions = groups ? groups.map(g => ({
    group: g,
    items: options.filter(o => o.group === g)
  })).filter(g => g.items.length > 0) : null;

  const renderOption = (opt) => (
    <button key={opt.key}
      type="button"
      role="option"
      aria-selected={value === opt.key}
      onClick={() => { onChange(opt.key); setOpen(false); }}
      style={{
        display: 'block', width: '100%', textAlign: 'left', border: 'none',
        padding: '0.375rem 0.625rem', cursor: 'pointer', fontSize: '0.9375rem', color: '#374151',
        backgroundColor: value === opt.key ? `${color}15` : 'transparent',
        fontWeight: value === opt.key ? '600' : '400'
      }}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${color}10`}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = value === opt.key ? `${color}15` : 'transparent'}
      onFocus={(e) => e.currentTarget.style.backgroundColor = `${color}10`}
      onBlur={(e) => e.currentTarget.style.backgroundColor = value === opt.key ? `${color}15` : 'transparent'}
    >
      {opt.label}
    </button>
  );

  return (
    <div ref={ref} style={{ position: 'relative' }} onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', padding: '0.625rem 0.875rem', fontSize: '0.9375rem',
          border: `1px solid ${open ? color : '#D1D5DB'}`, borderRadius: '6px',
          backgroundColor: 'white', cursor: 'pointer', textAlign: 'left',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          color: '#374151'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentLabel}</span>
        <span aria-hidden="true" style={{ fontSize: '0.625rem', color: '#9CA3AF', marginLeft: '0.5rem' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div role="listbox" aria-label={label} style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          backgroundColor: 'white', border: '1px solid #D1D5DB', borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '320px', overflowY: 'auto',
          marginTop: '2px'
        }}>
          {groupedOptions ? groupedOptions.map(g => (
            <div key={g.group} role="group" aria-label={g.group}>
              <div style={{ padding: '0.375rem 0.625rem', fontSize: '0.6875rem', color: '#9CA3AF', fontWeight: '600', borderBottom: '1px solid #F3F4F6' }}>
                {g.group}
              </div>
              {g.items.map(renderOption)}
            </div>
          )) : options.map(renderOption)}
        </div>
      )}
    </div>
  );
};

const BenchmarkingMatrix = ({ onBack, onCountryClick }) => {
  const [hvhsData, setHvhsData] = useState([]);
  const [contextData, setContextData] = useState([]);
  const [incomeGroups, setIncomeGroups] = useState({});
  const [loading, setLoading] = useState(true);
  // OECD-taxonomy indicators: catalogue of available indicators + values by ISO code
  const [oecdMetrics, setOecdMetrics] = useState([]);
  const [oecdValues, setOecdValues] = useState({});

  // HVHS components (multi-select). Axis depends on the selected indicator group.
  const [yAxisMetrics, setYAxisMetrics] = useState(new Set(['overall']));
  // Single-select indicator — default to GDP per Capita (B2)
  const [selectedMetric, setSelectedMetric] = useState('gdpPerCapita');

  const [colorBy, setColorBy] = useState('income');
  const [selectedCountries, setSelectedCountries] = useState(new Set());
  const [focusedCountries, setFocusedCountries] = useState(new Set());
  const [previewCountry, setPreviewCountry] = useState(null);
  const [axisScale, setAxisScale] = useState('linear');

  const chartRef = useRef(null);

  useEffect(() => { loadAllData(); }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [hvhs, fiscal, wb, isoRows, oecdText] = await Promise.all([
        HVHSViz.loadAllCountriesData(),
        fetchCSV('/FiscalSpace.csv'),
        fetchCSV('/wb-incomegroup.csv'),
        fetchCSV('/country_iso_codes.csv'),
        fetchText('/Current/OECD_Dataset.csv')
      ]);
      setHvhsData(hvhs);
      setContextData(fiscal);
      const igMap = {};
      wb.forEach(row => {
        if (row.Code) igMap[row.Code] = { income: row['Income group'] || '', region: row.Region || '' };
      });
      setIncomeGroups(igMap);

      const { metrics, values } = buildOecdMetrics(oecdText, isoRows);
      setOecdMetrics(metrics);
      setOecdValues(values);
      const countries = HVHSViz.getAvailableCountries(hvhs);
      setSelectedCountries(new Set(countries));
    } catch (error) {
      console.error('Error loading benchmarking data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCSV = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) return [];
      const text = await response.text();
      const lines = text.split('\n');
      let headerLine = lines[0];
      if (headerLine.charCodeAt(0) === 0xFEFF) headerLine = headerLine.slice(1);
      const headers = headerLine.split(',').map(h => h.trim());
      const data = [];
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        const values = [];
        let cur = '', inQ = false;
        for (const ch of lines[i]) {
          if (ch === '"') inQ = !inQ;
          else if (ch === ',' && !inQ) { values.push(cur.trim()); cur = ''; }
          else cur += ch;
        }
        values.push(cur.trim());
        if (values.length >= headers.length) {
          const row = {};
          headers.forEach((h, idx) => { row[h] = values[idx] || ''; });
          data.push(row);
        }
      }
      return data;
    } catch { return []; }
  };

  const fetchText = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) return '';
      return await response.text();
    } catch { return ''; }
  };

  const mergedData = useMemo(() => {
    if (hvhsData.length === 0) return [];
    const countries = HVHSViz.getAvailableCountries(hvhsData);
    const result = [];
    countries.forEach(country => {
      const countryRows = hvhsData.filter(r => r.Country === country);
      if (countryRows.length === 0) return;
      const iso = countryRows[0]['ISO-Code'] || '';
      const entry = { name: country, iso };
      HVHS_COMPONENTS.forEach(comp => {
        const row = countryRows.find(r => r.Variable === comp.variable);
        entry[comp.key] = row ? parseFloat(row.Value) : null;
      });
      const ig = incomeGroups[iso] || {};
      entry.income = ig.income || 'Unknown';
      entry.region = ig.region || 'Unknown';
      // B1: Compute GDP per capita = (GDP in millions * 1,000,000) / population
      // Fallback reference data for countries not in FiscalSpace.csv (World Bank 2023)
      const GDP_PC_FALLBACK = {
        'Brazil': 10412.55,   // WB 2023: GDP $2.17T / pop 214.3M
        'Japan': 33950.29,    // WB 2023: GDP $4.21T / pop 124.0M
      };
      const latestFiscal = (variable) => {
        const rows = contextData.filter(r =>
          r.Country === country && r.Variable && r.Variable.trim() === variable
        );
        return rows.length > 0 ? parseNumeric(rows[rows.length - 1].Value) : null;
      };
      const gdp = latestFiscal('GDP');
      const pop = latestFiscal('Number of Inhabitants');
      if (gdp != null && pop != null && pop > 0) {
        entry.gdpPerCapita = (gdp * 1000000) / pop;
      } else if (GDP_PC_FALLBACK[country]) {
        entry.gdpPerCapita = GDP_PC_FALLBACK[country];
      } else {
        entry.gdpPerCapita = null;
      }

      // OECD-taxonomy indicators (latest year per country/variable)
      const oecdForCountry = oecdValues[iso] || {};
      Object.entries(oecdForCountry).forEach(([metricKey, rec]) => {
        entry[metricKey] = rec.value;
      });

      result.push(entry);
    });
    return result;
  }, [hvhsData, contextData, incomeGroups, oecdValues]);

  const allCountries = useMemo(() => mergedData.map(d => d.name).sort(), [mergedData]);

  // Every selectable indicator, grouped by subcategory. HVHS component scores
  // are no longer offered here (CR feedback, Sep 2026).
  const allMetricOptions = useMemo(() => {
    const contextGroup = METRIC_GROUPS.find(g => g.key === 'context');
    const derived = DERIVED_METRICS.map(m => ({ ...m, group: contextGroup.label }));
    return [...derived, ...oecdMetrics];
  }, [oecdMetrics]);

  const getMetricConfig = useCallback(() => {
    const match = allMetricOptions.find(m => m.key === selectedMetric);
    const conf = match || allMetricOptions[0] || DERIVED_METRICS[0];
    const group = METRIC_GROUPS.find(g => g.key === conf.groupKey) || METRIC_GROUPS[0];
    return { ...conf, hvhsAxis: group.hvhsAxis, group: group.label };
  }, [selectedMetric, allMetricOptions]);

  const filteredData = useMemo(() => {
    return mergedData.filter(d => selectedCountries.has(d.name));
  }, [mergedData, selectedCountries]);

  // Countries whose point carries a text label.
  const labelledCountries = useMemo(() => {
    const set = new Set(focusedCountries);
    if (focusedCountries.size === 0 && filteredData.length <= LABEL_ALL_THRESHOLD) {
      filteredData.forEach(d => set.add(d.name));
    }
    if (previewCountry) set.add(previewCountry);
    return set;
  }, [focusedCountries, filteredData, previewCountry]);

  const metricConf = getMetricConfig();
  const plottedMetricVals = useMemo(
    () => filteredData.map(d => d[metricConf.key]).filter(v => v != null),
    [filteredData, metricConf.key]
  );
  const logAllowed = plottedMetricVals.length > 0 && plottedMetricVals.every(v => v > 0);
  const effectiveScale = axisScale === 'log' && logAllowed ? 'log' : 'linear';

  // Render chart — one trace per selected Y component
  useEffect(() => {
    const el = chartRef.current;
    if (!el || filteredData.length === 0 || yAxisMetrics.size === 0) return;

    const mConf = getMetricConfig();
    // HVHS axis is fixed by the indicator subcategory: 'y' → HVHS dependent,
    // 'x' → HVHS independent.
    const hvhsOnX = mConf.hvhsAxis === 'x';
    const selectedYComps = HVHS_COMPONENTS.filter(c => yAxisMetrics.has(c.key));
    const multiY = selectedYComps.length > 1;

    // Map an (HVHS value, indicator value) pair onto the chart axes.
    const axisPair = (hvhsVal, metricVal) =>
      hvhsOnX ? { x: hvhsVal, y: metricVal } : { x: metricVal, y: hvhsVal };

    const fmtScore = (v) => v != null ? v.toFixed(2) : 'N/A';
    const isCurrency = mConf.key === 'gdpPerCapita' ||
      /usd|dollar|\$/i.test(mConf.unit || '');
    const fmtMetric = (v) => fmtFull(v, isCurrency);

    const hasFocus = focusedCountries.size > 0;
    const labelFor = (d) => labelledCountries.has(d.name) ? d.name : '';
    const sizeFor = (d) => labelledCountries.has(d.name) ? 18 : 12;
    const opacityFor = (d) => !hasFocus || labelledCountries.has(d.name) ? 0.9 : 0.45;

    const traces = [];

    if (multiY) {
      // Multiple Y components: same color per component, different shape per country
      // One trace per country-component pair, grouped by component in legend
      const countrySymbolMap = {};
      filteredData.forEach((d, i) => {
        countrySymbolMap[d.name] = COUNTRY_SYMBOLS[i % COUNTRY_SYMBOLS.length];
      });

      // Create traces: group by component so legend shows components with colors
      selectedYComps.forEach((yComp, si) => {
        const compColor = COMPONENT_COLORS[si % COMPONENT_COLORS.length];
        filteredData.forEach((d, ci) => {
          const symbol = countrySymbolMap[d.name];
          const pair = axisPair(d[yComp.key], d[mConf.key]);
          traces.push({
            type: 'scatter',
            mode: 'markers+text',
            name: yComp.label,
            legendgroup: yComp.key,
            showlegend: ci === 0, // only show component once in legend
            x: [pair.x],
            y: [pair.y],
            customdata: [d.name],
            text: [labelFor(d)],
            textposition: 'top center',
            textfont: { size: 12, color: compColor },
            marker: {
              size: sizeFor(d), color: compColor, symbol,
              line: { color: 'white', width: 1.5 }, opacity: opacityFor(d)
            },
            hovertemplate: `<b>${d.name}</b><br>${yComp.label}: ${fmtScore(d[yComp.key])}<br>${mConf.label}: ${fmtMetric(d[mConf.key])}<extra></extra>`
          });
        });
      });

      // Add invisible traces for the shape legend (country key)
      filteredData.forEach((d, ci) => {
        traces.push({
          type: 'scatter',
          mode: 'markers',
          name: d.name + ' (shape)',
          legendgroup: `_country_${ci}`,
          x: [null], y: [null],
          marker: { size: 12, color: '#9CA3AF', symbol: COUNTRY_SYMBOLS[ci % COUNTRY_SYMBOLS.length] },
          showlegend: true,
          hoverinfo: 'skip'
        });
      });
    } else {
      // Single Y component: color + symbol by income/region (B7)
      const yComp = selectedYComps[0];
      const colorMap = colorBy === 'income' ? INCOME_COLORS : REGION_COLORS;
      const symbolMap = colorBy === 'income' ? INCOME_SYMBOLS : REGION_SYMBOLS;
      const groupKey = colorBy === 'income' ? 'income' : 'region';
      const groupLabel = colorBy === 'income' ? 'Income group' : 'Region';
      const groups = {};
      filteredData.forEach(d => {
        const group = d[groupKey] || 'Unknown';
        if (!groups[group]) groups[group] = [];
        groups[group].push(d);
      });
      Object.entries(groups).forEach(([groupName, points]) => {
        traces.push({
          type: 'scatter',
          mode: 'markers+text',
          name: groupName,
          x: points.map(d => axisPair(d[yComp.key], d[mConf.key]).x),
          y: points.map(d => axisPair(d[yComp.key], d[mConf.key]).y),
          customdata: points.map(d => d.name),
          text: points.map(labelFor),
          textposition: 'top center',
          textfont: { size: 12, color: '#1F2937' },
          marker: {
            size: points.map(sizeFor), color: colorMap[groupName] || '#6B7280',
            symbol: symbolMap[groupName] || 'circle',
            line: { color: 'white', width: 1.5 }, opacity: points.map(opacityFor)
          },
          hovertemplate: points.map(d =>
            `<b>${d.name}</b><br>${yComp.label}: ${fmtScore(d[yComp.key])}<br>${mConf.label}: ${fmtMetric(d[mConf.key])}<br>${groupLabel}: ${d[groupKey]}<extra></extra>`
          )
        });
      });
    }

    const metricVals = filteredData.map(d => d[mConf.key]).filter(v => v != null);
    const mMin = metricVals.length > 0 ? Math.min(...metricVals) : 0;
    const mMax = metricVals.length > 0 ? Math.max(...metricVals) : 10;
    const mPad = (mMax - mMin) * 0.15 || 1;

    // B5: HVHS axis is always fixed 0-5 (no auto-scaling)
    const hvhsLabel = multiY ? 'High value health system component score (0–5)' : `${selectedYComps[0].label} (0–5)`;
    const metricLabel = mConf.unit ? `${mConf.label} (${mConf.unit})` : mConf.label;

    const hvhsAxisConf = {
      title: { text: hvhsLabel, font: { size: 15, color: '#374151' } },
      range: [0, 5.2],
      gridcolor: '#E5E7EB', tickfont: { size: 13, color: '#6B7280' }, zeroline: false,
      tickvals: [0, 1, 2, 3, 4, 5],
      ticktext: ['0', '1', '2', '3', '4', '5']
    };

    const useLog = effectiveScale === 'log';
    let metricAxisConf;
    if (useLog) {
      const lo = mMin / 1.3, hi = mMax * 1.3;
      const ticks = logTicks(mMin, mMax);
      metricAxisConf = {
        title: { text: `${metricLabel} — log scale`, font: { size: 15, color: '#374151' } },
        type: 'log',
        range: [Math.log10(lo), Math.log10(hi)],
        tickvals: ticks,
        ticktext: ticks.map(t => fmtCompact(t, isCurrency)),
        gridcolor: '#E5E7EB', tickfont: { size: 13, color: '#6B7280' }, zeroline: false
      };
    } else {
      // keep the floor at 0 for non-negative indicators; allow negatives (e.g. GDP growth)
      const lo = mMin < 0 ? mMin - mPad : Math.max(0, mMin - mPad);
      const hi = mMax + mPad;
      const ticks = linearTicks(lo, hi);
      metricAxisConf = {
        title: { text: metricLabel, font: { size: 15, color: '#374151' } },
        type: 'linear',
        range: [lo, hi],
        tickvals: ticks,
        ticktext: ticks.map(t => fmtCompact(t, isCurrency)),
        gridcolor: '#E5E7EB', tickfont: { size: 13, color: '#6B7280' }, zeroline: false
      };
    }

    const layout = {
      xaxis: hvhsOnX ? hvhsAxisConf : metricAxisConf,
      yaxis: hvhsOnX ? metricAxisConf : hvhsAxisConf,
      showlegend: true,
      legend: {
        x: 1.02, y: 1, xanchor: 'left',
        bgcolor: 'rgba(255,255,255,0.9)', bordercolor: '#E5E7EB', borderwidth: 1,
        font: { size: 13 }
      },
      paper_bgcolor: 'white', plot_bgcolor: '#FAFAFA',
      margin: { t: 30, b: 80, l: 80, r: 20 },
      height: 600, autosize: true, hovermode: 'closest'
    };

    Plotly.react(el, traces, layout, {
      responsive: true, displayModeBar: false, displaylogo: false
    });

    // Click on data point to navigate to country page
    if (typeof el.removeAllListeners === 'function') el.removeAllListeners('plotly_click');
    el.on('plotly_click', (eventData) => {
      if (eventData && eventData.points && eventData.points.length > 0) {
        const point = eventData.points[0];
        const name = point.customdata;
        const matchedCountry = filteredData.find(d => d.name === name);
        if (matchedCountry && onCountryClick) {
          onCountryClick(matchedCountry.name);
        }
      }
    });
  }, [filteredData, yAxisMetrics, selectedMetric, colorBy, getMetricConfig, onCountryClick, labelledCountries, focusedCountries, effectiveScale]);

  const selectAll = () => setSelectedCountries(new Set(allCountries));
  const selectNone = () => { setSelectedCountries(new Set()); setFocusedCountries(new Set()); };

  const pickCountry = (country) => {
    setSelectedCountries(prev => prev.has(country) ? prev : new Set(prev).add(country));
    setFocusedCountries(prev => new Set(prev).add(country));
  };

  const toggleFocus = (country) => {
    setFocusedCountries(prev => {
      const next = new Set(prev);
      if (next.has(country)) next.delete(country); else next.add(country);
      return next;
    });
  };

  const hideCountry = (country) => {
    setSelectedCountries(prev => { const next = new Set(prev); next.delete(country); return next; });
    setFocusedCountries(prev => { const next = new Set(prev); next.delete(country); return next; });
    setPreviewCountry(p => (p === country ? null : p));
  };

  // Only offer indicators that have at least one value across the loaded countries.
  const metricsWithData = useMemo(() => {
    return allMetricOptions.filter(m => mergedData.some(d => d[m.key] != null));
  }, [allMetricOptions, mergedData]);

  const allShown = allCountries.length > 0 && selectedCountries.size === allCountries.length;
  const chipList = useMemo(() => {
    const focused = allCountries.filter(c => focusedCountries.has(c) && selectedCountries.has(c));
    const rest = allShown ? [] : allCountries.filter(c => selectedCountries.has(c) && !focusedCountries.has(c));
    return [...focused, ...rest];
  }, [allCountries, focusedCountries, selectedCountries, allShown]);

  if (loading) {
    return (
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '3rem', textAlign: 'center', color: '#6B7280' }} role="status">
        Loading benchmarking data...
      </div>
    );
  }

  const noMetricData = filteredData.length > 0 && filteredData.every(d => d[metricConf.key] == null);

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          <div style={{ flex: '1 1 480px' }}>
            <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#1F2937', margin: '0 0 0.375rem 0', textAlign: 'left' }}>
              Benchmarking Matrix
            </h2>
            <p style={{ fontSize: '1rem', color: '#6B7280', margin: 0, lineHeight: 1.5, textAlign: 'left' }}>
              Benchmarking metrics lets users compare high value health system scores with a selected health system indicator across countries.
            </p>
          </div>
          <button type="button" onClick={onBack} style={{
            padding: '0.625rem 1.25rem', backgroundColor: '#6B7280', color: 'white',
            border: 'none', borderRadius: '8px', fontSize: '0.9375rem', fontWeight: '500',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0
          }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4B5563'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6B7280'}
          >
            &larr; Back to Score Map
          </button>
        </div>

        {/* Main Layout: Rigid Sidebar + Chart (matching HSP design) */}
        <div style={{ display: 'flex', flexDirection: 'row', gap: 0, borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
          {/* Sidebar — unified dark panel */}
          <div style={{
            width: '320px',
            flexShrink: 0,
            background: 'linear-gradient(180deg, #113743 0%, #0a2029 100%)',
            color: 'white',
            padding: '1.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
            overflowY: 'auto'
          }}>
            {/* HVHS component */}
            <div>
              <h4 style={sidebarHeading}>High value health system component</h4>
              <MultiSelectDropdown
                label="High value health system component"
                options={HVHS_COMPONENTS}
                selected={yAxisMetrics}
                onChange={setYAxisMetrics}
                color={ACCENT}
              />
              {yAxisMetrics.size > 1 && (
                <p style={{ fontSize: '0.6875rem', color: ACCENT, margin: '0.5rem 0 0 0', fontStyle: 'italic' }}>
                  {yAxisMetrics.size} high value health system components selected — each shown as a separate series
                </p>
              )}
            </div>

            <div style={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.1)' }} />

            {/* Indicator */}
            <div>
              <h4 style={sidebarHeading}>Indicator</h4>
              <SingleSelectDropdown
                label="Indicator"
                options={metricsWithData}
                value={selectedMetric}
                onChange={setSelectedMetric}
                color={ACCENT}
                groups={GROUP_LABELS}
              />
              <p style={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.6)', margin: '0.5rem 0 0 0', fontStyle: 'italic' }}>
                {metricConf.group} — high value health system component on the {metricConf.hvhsAxis === 'x' ? 'X axis (independent)' : 'Y axis (dependent)'}; indicator on the {metricConf.hvhsAxis === 'x' ? 'Y' : 'X'} axis
              </p>

              <div style={{ marginTop: '0.875rem' }} role="group" aria-label="Indicator axis scale">
                <p style={{ fontSize: '0.75rem', color: '#E2E8F0', fontWeight: '600', margin: '0 0 0.375rem 0' }}>Indicator axis scale</p>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {['linear', 'log'].map(opt => {
                    const disabled = opt === 'log' && !logAllowed;
                    const active = effectiveScale === opt;
                    return (
                      <button key={opt} type="button" aria-pressed={active} disabled={disabled}
                        onClick={() => setAxisScale(opt)}
                        style={{ ...pillButton(active, disabled), flex: 1 }}>
                        {opt === 'linear' ? 'Linear' : 'Log'}
                      </button>
                    );
                  })}
                </div>
                {!logAllowed && (
                  <p style={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.6)', margin: '0.375rem 0 0 0', fontStyle: 'italic' }}>
                    {plottedMetricVals.length === 0
                      ? 'Log scale needs indicator values greater than zero.'
                      : 'Log scale is unavailable: some plotted indicator values are zero or negative.'}
                  </p>
                )}
                {axisScale === 'log' && !logAllowed && (
                  <p style={{ fontSize: '0.6875rem', color: ACCENT, margin: '0.25rem 0 0 0', fontStyle: 'italic' }}>
                    Showing linear scale until all plotted values are above zero.
                  </p>
                )}
              </div>
            </div>

            <div style={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.1)' }} />

            {/* Color By — only shown when single Y component */}
            {yAxisMetrics.size <= 1 && (
              <>
                <div role="group" aria-label="Color points by">
                  <h4 style={sidebarHeading}>Color points by</h4>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {['income', 'region'].map(opt => (
                      <button key={opt} type="button" aria-pressed={colorBy === opt} onClick={() => setColorBy(opt)} style={{
                        flex: 1, padding: '0.625rem', fontSize: '0.875rem',
                        border: '1px solid',
                        borderColor: colorBy === opt ? ACCENT : 'rgba(255,255,255,0.2)',
                        backgroundColor: colorBy === opt ? 'rgba(17,55,67,0.6)' : 'rgba(255,255,255,0.1)',
                        color: colorBy === opt ? ACCENT : '#CBD5E1',
                        borderRadius: '6px', cursor: 'pointer', fontWeight: colorBy === opt ? '600' : '400',
                        transition: 'all 0.2s'
                      }}>
                        {opt === 'income' ? 'Income group' : 'Region'}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.1)' }} />
              </>
            )}

            {/* Countries */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '0.5rem' }}>
                <h4 style={{ ...sidebarHeading, margin: 0 }}>Countries</h4>
                <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }} aria-live="polite">
                  {selectedCountries.size} of {allCountries.length} shown
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.625rem' }} role="group" aria-label="Country selection">
                <button type="button" onClick={selectAll} disabled={allShown}
                  aria-label="Show all countries"
                  style={{ ...pillButton(false, allShown), flex: 1 }}>
                  All
                </button>
                <button type="button" onClick={selectNone} disabled={selectedCountries.size === 0}
                  aria-label="Hide all countries"
                  style={{ ...pillButton(false, selectedCountries.size === 0), flex: 1 }}>
                  None
                </button>
              </div>
              <label htmlFor="bm-country-search" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                Add a country to the chart
              </label>
              <CountryTypeahead
                countries={allCountries}
                value=""
                onSelect={(c) => { if (c) pickCountry(c); }}
                inputId="bm-country-search"
                  width="100%"
              />

              {chipList.length > 0 && (
                <ul aria-label="Selected countries" style={{
                  listStyle: 'none', margin: '0.625rem 0 0 0', padding: '0.5rem',
                  display: 'flex', flexWrap: 'wrap', gap: '0.375rem', alignContent: 'flex-start',
                  maxHeight: '200px', overflowY: 'auto',
                  border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.04)'
                }}>
                  {chipList.map(c => {
                    const isFocused = focusedCountries.has(c);
                    return (
                      <li key={c} style={{
                        display: 'inline-flex', alignItems: 'stretch', borderRadius: '999px', overflow: 'hidden',
                        border: `1px solid ${isFocused ? ACCENT : 'rgba(255,255,255,0.3)'}`,
                        backgroundColor: isFocused ? ACCENT : 'rgba(255,255,255,0.08)'
                      }}>
                        <button type="button"
                          aria-pressed={isFocused}
                          aria-label={`${c}: ${isFocused ? 'labelled on chart, press to remove label' : 'press to label on chart'}`}
                          onClick={() => toggleFocus(c)}
                          onMouseEnter={() => setPreviewCountry(c)}
                          onMouseLeave={() => setPreviewCountry(p => (p === c ? null : p))}
                          onFocus={() => setPreviewCountry(c)}
                          onBlur={() => setPreviewCountry(p => (p === c ? null : p))}
                          style={{
                            border: 'none', background: 'transparent', cursor: 'pointer',
                            padding: '0.2rem 0.25rem 0.2rem 0.625rem', fontSize: '0.75rem', fontWeight: '600',
                            color: isFocused ? '#0a2029' : '#E2E8F0', minHeight: '26px'
                          }}>
                          {c}
                        </button>
                        <button type="button"
                          aria-label={`Hide ${c} from chart`}
                          title={`Hide ${c} from chart`}
                          onClick={() => hideCountry(c)}
                          style={{
                            border: 'none', background: 'transparent', cursor: 'pointer',
                            padding: '0 0.5rem 0 0.25rem', fontSize: '0.875rem', lineHeight: 1,
                            color: isFocused ? '#0a2029' : 'rgba(255,255,255,0.7)', minWidth: '26px'
                          }}>
                          ×
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* Chart Area */}
          <div style={{ flex: 1, backgroundColor: '#f9fafb', padding: '1.5rem', position: 'relative', minHeight: '500px' }}>
            {filteredData.length === 0 || yAxisMetrics.size === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px', color: '#9CA3AF', fontSize: '1rem' }} role="status">
                {yAxisMetrics.size === 0 ? 'Select at least one high value health system component' : mergedData.length === 0 ? 'No data available' : 'Select countries to display'}
              </div>
            ) : (
              <>
                {noMetricData && (
                  <div role="status" style={{
                    backgroundColor: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: '6px',
                    padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.8125rem', color: '#92400E'
                  }}>
                    No "{metricConf.label}" data available for the selected countries. Try another indicator or widen the country selection.
                  </div>
                )}
                <div ref={chartRef} style={{ width: '100%' }} tabIndex={0}
                  role="figure"
                  aria-label={`Scatter plot of ${yAxisMetrics.size > 1 ? 'high value health system component scores' : (HVHS_COMPONENTS.find(c => yAxisMetrics.has(c.key)) || {}).label} against ${metricConf.label} for ${filteredData.length} countries`} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BenchmarkingMatrix;
